import clsx from 'clsx'

import { Log } from '@/types/logs'
import { OfficialReportType } from '@/types/officialReport'
import { useGetEmployees } from '@/modules/Auth/api/useGetEmployees'
import { parseDate } from '@/helpers/time'
import { getLogCommonInformation } from '@/helpers/logInformation'
import {
    isHeadwayOfficialReportType,
    isLateOfficialReportType,
    isGenericOfficialReportType,
} from '../helpers/checkReportType'
import { getLateReportTypeLabel } from '../helpers/getLateReportTypeLabel'
import { useGetCodeName } from '@/modules/CombinedLogs/hooks/useGetCodeName'
import { OFFICIAL_REPORT_TYPE_MAP } from '../constant'

import styles from './OfficialReportModal.module.scss'

interface Props {
    log: Log
    type: OfficialReportType
}

export const OfficialReportReadOnly = ({ log, type }: Props) => {
    const { baseDepot, duty, routes, run, fleet, trip } = getLogCommonInformation(log)
    const { data: employees = [] } = useGetEmployees()
    const getCodeName = useGetCodeName()

    const selectedDriver = employees.find((item) => item.badgeNumber === log.report?.driverBadgeNumber)

    const getReportType = () =>
        type in OFFICIAL_REPORT_TYPE_MAP ? OFFICIAL_REPORT_TYPE_MAP[type] : getCodeName(log.code)

    if (!log.report) {
        return null
    }

    return (
        <div className={clsx(styles.content, styles.readOnlyContent)}>
            <div>
                <h1 className={styles.title}>Official report - {getReportType()}</h1>
                {log.codeTypeReasonInfo && isGenericOfficialReportType(type) && (
                    <p className={styles.description}>{log.codeTypeReasonInfo.reasonDescription}</p>
                )}
            </div>
            <div className={styles.contentWrapper}>
                <div className={styles.informationWrapper}>
                    <span className={styles.label}>Official writing report</span>
                    {log.report.userShift?.badgeNumber ? (
                        <span className={styles.text}>Badge no. {log.report.userShift.badgeNumber}</span>
                    ) : (
                        <span className={styles.text}>{log.report.userShift.authorName}</span>
                    )}
                </div>
                <div className={styles.informationContainer}>
                    <div className={styles.informationWrapper}>
                        <span className={styles.label}>Date of incident</span>
                        <span className={styles.text}>{parseDate(log.report.incidentDate).date}</span>
                    </div>
                    {isGenericOfficialReportType(type) && (
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Time of incident</span>
                            <span className={styles.text}>{parseDate(log.report.incidentDate).time}</span>
                        </div>
                    )}
                </div>
                {isLateOfficialReportType(type) && (
                    <>
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Time</span>
                            <span className={styles.text}>{log.report.takeOverTimeMode}</span>
                        </div>
                        <div className={styles.informationContainer}>
                            <div className={styles.informationWrapper}>
                                <span className={styles.label}>
                                    {getLateReportTypeLabel(type, log.report.takeOverTimeMode)}
                                </span>
                                <span className={styles.text}>{parseDate(log.report.takeOverDate).time}</span>
                            </div>
                            <div className={styles.informationWrapper}>
                                <span className={styles.label}>Actual takeover time</span>
                                <span className={styles.text}>{parseDate(log.report.actualTakeOverDate).time}</span>
                            </div>
                        </div>
                    </>
                )}
                {isHeadwayOfficialReportType(type) &&
                    log.report.headways.map((item, index) => (
                        <div key={index} className={styles.informationContainer}>
                            <div className={styles.informationWrapper}>
                                <span className={styles.label}>Headway</span>
                                <span className={styles.text}>{item.headway}</span>
                            </div>
                            <div className={styles.informationWrapper}>
                                <span className={styles.label}>Bus stop</span>
                                <span className={styles.text}>{item.busStop}</span>
                            </div>
                            <div className={styles.informationWrapper}>
                                <span className={styles.label}>Time observed</span>
                                <span className={styles.text}>{parseDate(item.observedAt).time}</span>
                            </div>
                        </div>
                    ))}
            </div>
            <div className={styles.wrapper}>
                <p className={styles.subtitle}>Driver & vehicle</p>
                <div className={styles.contentWrapper}>
                    <div className={styles.informationWrapper}>
                        <span className={styles.label}>Base depot</span>
                        <span className={styles.text}>{baseDepot || '-'}</span>
                    </div>
                    <div className={styles.informationContainer}>
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Duty</span>
                            <span className={styles.text}>{duty || '-'}</span>
                        </div>
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Route</span>
                            <span className={styles.text}>{routes || '-'}</span>
                        </div>
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Running number</span>
                            <span className={styles.text}>{run || '-'}</span>
                        </div>
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Fleet</span>
                            <span className={styles.text}>{fleet || '-'}</span>
                        </div>
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Trip</span>
                            <span className={styles.text}>{trip || '-'}</span>
                        </div>
                    </div>
                </div>
                <div>
                    <div className={styles.informationWrapper}>
                        <span className={styles.label}>Driver employee number</span>
                        <span className={styles.text}>{log.report.driverBadgeNumber}</span>
                    </div>
                    {selectedDriver && (
                        <div className={styles.informationWrapper}>
                            <span className={styles.label}>Driver</span>
                            <span className={styles.text}>
                                {selectedDriver?.firstName} {selectedDriver?.lastName}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.wrapper}>
                <p className={styles.subtitle}>Incident details</p>
                <div className={styles.contentWrapper}>
                    <div className={styles.informationWrapper}>
                        <span className={styles.label}>Location of issue</span>
                        <span className={styles.text}>{log.report.issueLocation}</span>
                    </div>
                    <div className={styles.informationWrapper}>
                        <span className={styles.label}>Details</span>
                    </div>
                    <div className={styles.informationWrapper}>
                        <span
                            className={clsx(styles.text, styles.details)}
                            dangerouslySetInnerHTML={{ __html: log.report.description }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
